import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Shield, LogOut,
  Menu, X, ChevronDown, Bell, Activity
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import '../../styles/admin-layout.css';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/dashboard/users', icon: Users, label: 'User Management' },
  { to: '/dashboard/audit', icon: Activity, label: 'Audit Logs' },
];

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, signOut } = useAdminAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Admin';
  const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="admin-layout-root">
      {sidebarOpen && <div className="admin-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`admin-sidebar${sidebarOpen ? ' admin-sidebar-open' : ''}`}>
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-logo">
            <svg width="20" height="18" viewBox="0 0 32 28" fill="none">
              <rect x="0" y="8" width="6" height="20" rx="1" fill="#E8471E"/>
              <rect x="9" y="4" width="6" height="24" rx="1" fill="#E8471E"/>
              <rect x="18" y="0" width="6" height="28" rx="1" fill="#E8471E"/>
              <rect x="26" y="6" width="6" height="22" rx="1" fill="#C13010"/>
            </svg>
            <div>
              <span className="admin-sidebar-logo-text">SYNFIN</span>
              <span className="admin-sidebar-badge">ADMIN</span>
            </div>
          </div>
          <button className="admin-sidebar-close" onClick={() => setSidebarOpen(false)}>
            <X size={18} />
          </button>
        </div>

        <nav className="admin-nav">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) =>
                `admin-nav-item${isActive ? ' admin-nav-active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}>
              <Icon size={17} /><span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-version-badge">
            <Shield size={11} /> Admin Portal v1.0
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="admin-main">
        <header className="admin-topbar">
          <button className="admin-menu-btn" onClick={() => setSidebarOpen(true)}>
            <Menu size={20} />
          </button>

          <div className="admin-topbar-title">
            <Shield size={16} color="#E8471E" />
            <span>Administration</span>
          </div>

          <div className="admin-topbar-right">
            <button className="admin-topbar-icon-btn">
              <Bell size={17} />
            </button>

            <div style={{ position: 'relative' }}>
              <button className="admin-user-trigger"
                onClick={() => setUserMenuOpen(v => !v)}>
                <div className="admin-avatar">{initials}</div>
                <span className="admin-user-name">{displayName}</span>
                <ChevronDown size={14} />
              </button>

              {userMenuOpen && (
                <div className="admin-user-dropdown">
                  <div className="admin-dropdown-info">
                    <div className="admin-avatar">{initials}</div>
                    <div>
                      <p className="admin-dropdown-name">{displayName}</p>
                      <p className="admin-dropdown-role">Administrator</p>
                    </div>
                  </div>
                  <div className="admin-dropdown-divider" />
                  <button className="admin-dropdown-item admin-dropdown-danger"
                    onClick={handleSignOut}>
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="admin-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
